import type { UserRole } from '../types';
import type { QuestionSystemType } from './api';
import type { ProgressConfirmation } from './simulationProgress';

export type BulkCreateProgressHandler = (completed: number, total: number) => void;

export type LoginResponse = { 
  success: boolean;
  token: string; 
  user: { id: string; email: string; role: UserRole } 
};

export type StartTestResponse = { 
  success: boolean;
  test_id: string; 
  resumed?: boolean;
  progress?: { index: number; answers: any; revision?: number };
  questions: { 
    question_id: string; 
    question_text: string; 
    options: string[]; 
    media: string|null;
    level: number;
    part: string;
    correct_index: number;
    vocab_ids: number[];
    explanation: string | null;
    order?: number[]; // Add order field
  }[] 
};

export type ActiveTest = {
  id: string;
  date: string;
  last_active: string;
  level: number;
  part: string;
  total: number;
  current_index: number;
  progress_count: number;
	system_type?: QuestionSystemType;
	bank_version?: string;
	workbook_part?: string | null;
	paper_id?: string | null;
	paper_year?: number | null;
	paper_month?: number | null;
	topic_coarse?: string | null;
	topic_fine?: string | null;
	exam_duration_seconds?: number | null;
};

export type QuestionChangeRequest = {
  id: string;
  question_id: string;
  bank_version: '2.0' | '3.0';
  change_type: 'create' | 'update' | 'delete';
  source_role: UserRole;
  submitted_by: string;
  submitted_at: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  review_comment?: string | null;
  original_snapshot?: any;
  proposed_snapshot?: any;
};

export type SubmitTestResponse = { 
  success: boolean;
  test_id: string;
  score: number; 
  total: number; 
  percentage: number;
  weighted_score?: number;
  max_score?: number;
  section_scores?: {
    listening: number;
    reading: number;
    writing: number;
  };
};
export type SubmitAnswerItem = {
  question_id: string;
  chosen_option_index?: number | null;
  chose_buzhidao: boolean;
  time_spent_seconds?: number;
  chosen_text_answer?: string | null;
  chosen_sequence?: string[] | null;
};

export type TestHistoryResponse = {
  success: boolean;
  tests: {
    id: string;
    date: string;
    level: number;
    part: string;
    totalQuestions: number;
    score: number;
    percentage: number;
  }[];
};

export type PaginatedTestHistoryResponse = {
  success: boolean;
  history: any[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
};

export const API_BASE_URL = BACKEND_URL;
const LOCAL_API_BASE = API_BASE_URL;

console.log('API_BASE_URL initialized:', API_BASE_URL);
try { (window as any).DEBUG_API_URL = API_BASE_URL; } catch {}

async function request<T>(path: string, options: RequestInit = {}, token?: string): Promise<T> {
  const headers: Record<string,string> = { 
    'Content-Type': 'application/json', 
    ...(options.headers as any) 
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add timeout and signal for better error handling
  // const controller = new AbortController();
  // const timeoutId = setTimeout(() => controller.abort(), 60000); // Removed timeout to allow long processing
  
  try {
    const res = await fetch(`${LOCAL_API_BASE}${path}`, { 
      ...options, 
      headers,
      // signal: controller.signal
    });
    
    // clearTimeout(timeoutId);
    
    if (!res.ok) {
      let msg = `Request failed: ${res.status} ${res.statusText}`;
      try { 
        const j = await res.json(); 
        msg = j?.message || msg; 
      } catch {}
      throw new Error(msg);
    }
    
    if (res.status === 204) return undefined as any;
    return res.json();
  } catch (error) {
    // clearTimeout(timeoutId);
    console.error(`API request failed for ${path}:`, error);
    throw error;
  }
}

export const api = {
  // Health check to verify local server is running
  async healthCheck() {
    try {
      const response = await request<{ status: string; message: string }>('/health');
      return response;
    } catch (error) {
      console.error('API health check failed:', error);
      throw new Error('API server is not running.');
    }
  },

  // Authentication
  async login(identifier: string, password: string) {
    const isEmail = identifier.includes('@');
    const payload = isEmail ? { email: identifier, password } : { username: identifier, password };
    return request<LoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify(payload) });
  },

  // Start HSK 3 Listening Test
  async startTest(level: number, part: string, count: number, token: string, subparts?: string[], partCounts?: Array<{ part: string; count: number }>, options?: { bankVersion?: string; systemType?: QuestionSystemType; workbookPart?: string; paperId?: string; coarseType?: string; fineType?: string; test_session_id?: string; start_new?: boolean }) {
    return request<StartTestResponse>('/questions/start-test', { 
      method: 'POST', 
      body: JSON.stringify({ level, part, count, subparts, partCounts, ...options })
    }, token);
  },

  async saveProgress(test_session_id: string, current_index: number, answers: any, token: string,
    confirmation?: { save_id: string; answer_digest: string; answered_count: number; expected_revision: number; question_ids: string[] }) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try { return await request<ProgressConfirmation>('/test/progress', {
      method: 'POST',
      signal: controller.signal,
      body: JSON.stringify({ test_session_id, current_index, answers, ...confirmation })
    }, token); }
    catch (error) {
      if (error instanceof Error && error.name === 'AbortError') throw new Error('云端保存超时，本机答案仍保留，请重试。');
      throw error;
    } finally { clearTimeout(timeout); }
  },

  async getSavedProgress(test_session_id: string, token: string) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try { return await request<ProgressConfirmation>(`/test/progress?test_session_id=${encodeURIComponent(test_session_id)}`, {
      cache: 'no-store', signal: controller.signal,
    }, token); }
    catch (error) {
      if (error instanceof Error && error.name === 'AbortError') throw new Error('云端核对超时，本机答案仍保留，请重试。');
      throw error;
    } finally { clearTimeout(timeout); }
  },

  async getActiveTests(token: string) {
    return request<{ success: boolean; active: ActiveTest[] }>('/user/active', {}, token);
  },

  // Get questions for any HSK test (wrapper around startTest)
  async getQuestions(level: number, part: string, count: number) {
    try {
      const response = await request<StartTestResponse>('/questions/start-test', { 
        method: 'POST', 
        body: JSON.stringify({ level, part, count }) 
      });
      
      // Transform the response to match the expected format
      return response.questions.map(q => ({
        id: q.question_id,
        level: q.level,
        part: q.part,
        question: q.question_text,
        options: q.options,
        correctAnswer: q.correct_index,
        media: q.media,
        vocabIds: q.vocab_ids,
        explanation: q.explanation
      }));
    } catch (error) {
      console.error('Failed to get questions:', error);
      throw error;
    }
  },

  // Submit test results
  async submitTest(
    session_type: string, 
    answers: SubmitAnswerItem[], 
    token: string
  ) {
    return request<SubmitTestResponse>('/test/submit', { 
      method: 'POST', 
      body: JSON.stringify({ test_session_id: session_type, answers, token }) 
    }, token);
  },

  // Get test history
  async getHistory(token: string) {
    return request<TestHistoryResponse>('/user/history', {}, token);
  },

  async getHistoryPage(token: string, options: { page?: number; limit?: number; bankVersion?: string; level?: string; systemType?: string; filterType?: string; category?: string } = {}) {
    const params = new URLSearchParams({
      paginated: '1',
      page: String(options.page || 1),
      limit: String(options.limit || 5),
    });
    if (options.bankVersion && options.bankVersion !== 'all') params.set('bankVersion', options.bankVersion);
    if (options.level && options.level !== 'all') params.set('level', options.level);
    if (options.systemType && options.systemType !== 'all') params.set('systemType', options.systemType);
    params.set('includeIncomplete', '1');
    if (options.category) params.set('category', options.category);
    if (options.filterType && options.filterType !== 'all') params.set('filterType', options.filterType);
    return request<PaginatedTestHistoryResponse>(`/user/history?${params.toString()}`, {}, token);
  },

  async browseQuestions(params: { level?: number; part?: string; qid?: string; limit?: number; offset?: number; bankVersion?: string }, token?: string) {
    const q = new URLSearchParams();
    if (params.level != null) q.set('level', String(params.level));
    if (params.part) q.set('part', params.part);
    if (params.qid) q.set('qid', params.qid);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    if (params.bankVersion) q.set('bankVersion', params.bankVersion);
    return request<{ success: boolean; total: number; questions: any[] }>(`/questions/browse?${q.toString()}`, {}, token);
  },

  async listParts(level?: number, token?: string, bankVersion?: string) {
    const q = new URLSearchParams();
    if (level != null) q.set('level', String(level));
    if (bankVersion) q.set('bankVersion', bankVersion);
    return request<{ success: boolean; parts: Array<{ part: string; cnt: number }> }>(`/questions/parts?${q.toString()}`, {}, token);
  },

  async deleteQuestion(id: string, token?: string, bankVersion?: string) {
    const q = new URLSearchParams();
    if (bankVersion) q.set('bankVersion', bankVersion);
    const suffix = q.toString() ? `?${q.toString()}` : '';
    return request<{ success: boolean; deleted: number }>(`/questions/${id}${suffix}`, { method: 'DELETE' }, token);
  },

  async deleteQuestions(ids: string[], token?: string) {
    return request<{ success: boolean; deleted: number }>(`/questions/delete-batch`, { method: 'POST', body: JSON.stringify({ ids }) }, token);
  },

  async updateQuestion(id: string, payload: any, token?: string, bankVersion?: string) {
    return request<{ success: boolean; updated: number }>(`/questions/${id}`, { method: 'PUT', body: JSON.stringify({ ...payload, bankVersion }) }, token);
  },

  async createQuestion(payload: any, token?: string, bankVersion?: string) {
    return request<{ success: boolean; inserted: number }>(`/questions`, { method: 'POST', body: JSON.stringify({ ...payload, bankVersion }) }, token);
  },

  async createQuestionsBulk(
    items: any[],
    token?: string,
    bankVersion?: string,
    options?: { chunkSize?: number; onProgress?: BulkCreateProgressHandler }
  ) {
    const normalizedItems = Array.isArray(items) ? items : [];
    if (normalizedItems.length === 0) {
      throw new Error('批量新增内容为空');
    }

    const chunkSize = Math.max(1, Math.min(options?.chunkSize ?? 200, 500));
    let inserted = 0;

    for (let start = 0; start < normalizedItems.length; start += chunkSize) {
      const chunk = normalizedItems.slice(start, start + chunkSize);
      const body = { items: chunk, bankVersion };
      const response = await request<{ success: boolean; inserted: number }>(
        `/questions/bulk`,
        { method: 'POST', body: JSON.stringify(body) },
        token
      );
      inserted += Number(response?.inserted || 0);
      options?.onProgress?.(Math.min(start + chunk.length, normalizedItems.length), normalizedItems.length);
    }

    return { success: true, inserted };
  },

  async createQuestionChangeRequest(payload: { question_id?: string; bankVersion?: string; change_type: 'create' | 'update' | 'delete'; proposed_snapshot?: any }, token?: string) {
    return request<{ success: boolean; request: QuestionChangeRequest }>(`/question-change-requests`, {
      method: 'POST',
      body: JSON.stringify(payload)
    }, token);
  },

  async getMyQuestionChangeRequests(token?: string) {
    return request<{ success: boolean; requests: QuestionChangeRequest[] }>(`/question-change-requests/mine`, {}, token);
  },

  async getAdminQuestionChangeRequests(token?: string, status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'all' = 'pending') {
    return request<{ success: boolean; requests: QuestionChangeRequest[] }>(`/admin/question-change-requests?status=${encodeURIComponent(status)}`, {}, token);
  },

  async approveQuestionChangeRequest(id: string, token?: string) {
    return request<{ success: boolean; request: QuestionChangeRequest }>(`/admin/question-change-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({})
    }, token);
  },

  async rejectQuestionChangeRequest(id: string, reviewComment: string, token?: string) {
    return request<{ success: boolean; request: QuestionChangeRequest }>(`/admin/question-change-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ review_comment: reviewComment })
    }, token);
  },

  async uploadMedia(key: string, file: File, token?: string) {
    const fd = new FormData();
    fd.append('key', key);
    fd.append('file', file);
    const headers: Record<string,string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${LOCAL_API_BASE}/media/upload`, { method: 'POST', body: fd, headers });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listMedia(prefix: string, token?: string) {
    const headers: Record<string,string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${LOCAL_API_BASE}/media/list?prefix=${encodeURIComponent(prefix)}`, { headers });
    if (!res.ok) throw new Error(await res.text());
    return res.json() as Promise<{ objects: Array<{ key: string; size: number; uploaded?: string }>, truncated: boolean, cursor: string | null }>;
  },

  async deleteMedia(key: string, token?: string) {
    const headers: Record<string,string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${LOCAL_API_BASE}/media?key=${encodeURIComponent(key)}`, { method: 'DELETE', headers });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async renameMedia(key: string, newKey: string, token?: string) {
    const headers: Record<string,string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${LOCAL_API_BASE}/media/rename`, { method: 'POST', headers, body: JSON.stringify({ key, newKey }) });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // Utility method to check if API is available
  async isLocalApiAvailable(): Promise<boolean> {
    try {
      await this.healthCheck();
      return true;
    } catch {
      return true; // Always assume available for remote to avoid blocking UI
    }
  }
};

export const localApi = api;
export const initializeLocalApi = async (): Promise<boolean> => {
  return true; // Always return true for remote API
};

// Submit with automatic retry + offline queue fallback
export async function submitWithRetry(
  testSessionId: string,
  answers: SubmitAnswerItem[],
  token: string
): Promise<SubmitTestResponse> {
  const { addPendingSubmission } = await import('./pendingSubmissions');
  const maxAttempts = 3;
  let lastErr: unknown;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await api.submitTest(testSessionId, answers, token);
    } catch (err) {
      lastErr = err;
      if (attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, attempt * 1500));
      }
    }
  }

  // All retries exhausted — save to pending queue
  addPendingSubmission(testSessionId, answers, token);
  throw lastErr;
}
